<script setup lang="ts">
import type { Task } from '~/models/AssignTask'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { KsButton, KsIcon, KsInput } from '@aschehoug/kloss'
import { debounce } from '~/utils/functionUtils'
import useAssignTaskDialog from '~/stores/assignTaskDialog'
import { TaskStatus } from '~/models/AssignTask'
import { useAssignTask } from '~/composables/useAssignTask'
import ScrollBox from '~/components/utils/ScrollBox.vue'
import StudentEditItem from '~/components/products/exams/assignment/StudentEditItem.vue'

const { task } = defineProps<{ task: Task }>()

const { t } = useI18n()
const { closeTask } = useAssignTask()
const { setDialogOpen } = useAssignTaskDialog()
const { taskHasBeenClosed } = storeToRefs(useAssignTaskDialog())

const searchQuery = defineModel<string>({ default: '' })
const isScrolled = ref(false)
const isLoadingList = ref(false)

const filteredStudents = computed(() => task.users ? task.users
  .filter(student => (student.fullName ?? '').toLowerCase().includes(searchQuery.value.toLowerCase()))
  .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '')) : [])

const studentsOpen = computed(() => filteredStudents.value?.filter(student => student.status === TaskStatus.Open) ?? [])
const studentsClosed = computed(() => filteredStudents.value?.filter(student => student.status === TaskStatus.Closed) ?? [])

const allUsersStatus = computed(() => {
  const allOpen = studentsOpen.value.length === filteredStudents.value.length
  const allClosed = studentsClosed.value.length === filteredStudents.value.length
  return allOpen || allClosed
})

const handleScroll = (e: Event) => {
  const target = e.target as HTMLElement
  isScrolled.value = target.scrollTop > 0
}

const onInput = debounce((event: Event) => {
  searchQuery.value = (event.target as HTMLInputElement).value
  isLoadingList.value = false
}, 500)

const handleInput = (event: Event) => {
  isLoadingList.value = true
  onInput(event)
}

watch(() => task.users, async () => {
  if (!task.taskId || !task.users?.length) return
  if (task.users.every(student => student.status === TaskStatus.Closed)) {
    await closeTask.mutateAsync(task)
    setDialogOpen(task.taskId, 'assign')
    taskHasBeenClosed.value[task.taskId] = true
  }
})
</script>

<template>
  <div class="rounded-md bg-gray-5 pt-4">
    <div
      class="flex w-full items-center justify-between px-4 pb-4 transition-all"
      :class="{ 'shadow-md': isScrolled }"
    >
      <div class="relative w-full md:w-1/2">
        <div class="absolute left-5 top-1/2 z-10 flex -translate-y-1/2 justify-center text-base text-gray-40">
          <KsIcon
            icon="magnifying-glass"
            :fade="isLoadingList"
            animation-duration="500ms"
            loop-animation
          />
        </div>
        <KsInput
          :value="searchQuery"
          type="text"
          name="search"
          style="--ks-input: white; --ks-inputhover: white"
          :placeholder="t('assign.searchPlaceholder')"
          class="!rounded !pl-12 !text-base placeholder:text-gray-40"
          @input="handleInput"
        />
        <KsButton
          v-if="searchQuery.length"
          icon-left="xmark"
          shape="square"
          variant="tertiary"
          size="small"
          class="absolute right-3 top-1/2 z-10 -translate-y-1/2 text-xl"
          @click="searchQuery = ''"
        />
      </div>
    </div>
    <ScrollBox
      class="grid max-h-full grid-cols-1 gap-6 !overflow-x-hidden px-4 pb-4"
      :class="{
        'md:grid-cols-1': searchQuery.length,
        'md:grid-cols-2': !allUsersStatus
      }"
      @scroll="handleScroll"
    >
      <div v-if="studentsOpen.length">
        <p
          v-dompurify-html="t('assign.openFor')"
          class="text-sm font-medium text-gray-50"
        />
        <transition-group
          name="list"
          tag="ul"
          class="relative flex flex-col"
        >
          <StudentEditItem
            v-for="student in studentsOpen"
            :key="`student-${student.userId}`"
            :student
            :task
          />
        </transition-group>
      </div>
      <div v-if="studentsClosed.length">
        <p
          v-dompurify-html="t('assign.closedFor')"
          class="text-sm font-medium text-gray-50"
        />
        <transition-group
          name="list"
          tag="ul"
          class="relative flex flex-col"
        >
          <StudentEditItem
            v-for="student in studentsClosed"
            :key="`student-${student.userId}`"
            :student
            :task
          />
        </transition-group>
      </div>
      <div
        v-if="!filteredStudents.length"
        class="text-sm"
      >
        {{ t('assign.noStudents') }}
      </div>
    </ScrollBox>
  </div>
</template>

<style scoped>
.list-move,
.list-enter-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  transition: all 0.2s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}

.list-leave-active {
  position: absolute;
}
</style>
